/**
 * What a party looks like when you rest the pointer on it.
 *
 * The card in the grid has room for a title and a count; this is everything
 * else - who is in it, who is talking, the ranks it wants, and what happens if
 * you click. It is a preview, so nothing in here is interactive.
 */

import type { Party } from '@/lib/types';
import { cn } from '@/lib/cn';
import { useSession } from '@/stores/session';
import { Avatar } from './Avatar';
import { Icon } from '@/icons/Icon';
import { gameIconArt } from '@/lib/gameArt';
import { EmojiText } from './EmojiText';

export function PartyHoverBody({ party }: { party: Party }) {
  const catalog = useSession((s) => s.catalog);
  const game = catalog?.games.find((g) => g.id === party.gameId);
  const art = gameIconArt(party.gameId);
  const region = catalog?.regions.find((r) => r.id === party.region);

  const inVoice = new Set(party.voice?.members ?? []);
  // The host first, then everyone else in the order they joined.
  const members = [...party.members].sort((a, b) =>
    a.userId === party.hostId ? -1 : b.userId === party.hostId ? 1 : 0
  );
  const open = party.size - party.memberCount;
  const rank = party.minRank && party.maxRank
    ? party.minRank + ' – ' + party.maxRank
    : party.minRank || party.maxRank || 'Any rank';

  return (
    <div className="flex flex-col">
      <div
        className="flex items-center gap-2.5 border-b border-line-soft p-3"
        style={{ background: `linear-gradient(100deg, color-mix(in srgb, ${game?.color ?? 'var(--accent)'} 16%, transparent), transparent 70%)` }}
      >
        {/* The game's real icon, or nothing. The drawn glyph was a stand-in
            from before the artwork existed, and beside a title it read as a
            symbol that meant something. */}
        {art ? (
          <img
            src={art}
            alt=""
            className="h-9 w-9 flex-none rounded-[26%] object-cover"
          />
        ) : null}
        <div className="min-w-0">
          <div className="truncate text-[13.5px] font-bold"><EmojiText text={party.title} size={16} /></div>
          <div className="truncate text-[12px] text-mute">
            {game?.name ?? party.gameId} · {party.mode}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1.5 px-3 pt-2.5">
        <Fact icon="users" text={party.memberCount + '/' + party.size + ' in the party'} />
        <Fact icon="globe" text={region?.name ?? party.region} />
        <Fact icon="trending" text={rank} />
        {party.micRequired ? <Fact icon="mic" text="Microphone required" /> : null}
        {inVoice.size ? <Fact icon="headset" text={inVoice.size + ' in voice now'} tone="voice" /> : null}
      </div>

      <div className="px-3 pt-3 pb-1 text-[10px] font-bold tracking-[.08em] text-ghost uppercase">
        Who is here
      </div>
      <div className="flex max-h-[168px] flex-col gap-0.5 overflow-y-auto px-2 pb-1">
        {members.map((m) => {
          if (!m.user) return null;
          const owner = m.userId === party.hostId;
          return (
            <div key={m.userId} className="flex items-center gap-2 rounded-[calc(8px*var(--radius-mul,1))] px-1.5 py-1">
              <Avatar user={m.user} size={24} presence />
              <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold">
                {m.user.displayName}
              </span>
              {owner ? <Icon name="crown" size={12} className="flex-none text-mythic" /> : null}
              {inVoice.has(m.userId) ? (
                <Icon name="waves" size={12} className="flex-none text-voice" />
              ) : null}
            </div>
          );
        })}
      </div>

      <div
        className={cn(
          'mx-3 mt-1 rounded-[var(--radius-s)] px-2.5 py-1.5 text-center text-[12px] font-bold',
          open > 0
            ? 'bg-[color-mix(in_srgb,var(--good)_14%,transparent)] text-good'
            : 'bg-surface-3 text-mute'
        )}
      >
        {open > 0 ? open + (open === 1 ? ' slot open' : ' slots open') : 'Full'}
      </div>

      {party.description ? (
        <p className="mx-3 mt-2 line-clamp-3 text-[12px] leading-snug text-mute">
          <EmojiText text={party.description} size={15} />
        </p>
      ) : null}

      <div className="mt-2.5 border-t border-line-soft px-3 py-2 text-[11.5px] font-semibold text-mute">
        {party.isMember ? 'Click to open' : 'Click to join'}
      </div>
    </div>
  );
}

function Fact({ icon, text, tone }: { icon: string; text: string; tone?: 'voice' }) {
  return (
    <span className={cn('flex min-w-0 items-center gap-1.5 text-[12px]', tone === 'voice' ? 'text-voice' : 'text-dim')}>
      <Icon name={icon} size={12} className="flex-none opacity-70" />
      <span className="truncate">{text}</span>
    </span>
  );
}
